import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, CheckCircle, XCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import MainNav from '@/components/MainNav';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import Footer from '@/components/Footer';

const InstallTestPage = () => {
  const [testUrl, setTestUrl] = useState<string>('');
  const [installKey, setInstallKey] = useState<string>('');
  const [isTesting, setIsTesting] = useState<boolean>(false);
  const [testResults, setTestResults] = useState<any>(null);
  
  const handleTest = async () => {
    if (!testUrl || !installKey) {
      toast.error('Please enter both a URL and an installation key');
      return;
    }
    
    setIsTesting(true);
    setTestResults(null);
    
    try {
      // Normalize URL input
      let url = testUrl;
      if (!url.startsWith('http')) {
        url = 'https://' + url;
      }
      
      // Make sure it's a valid URL
      try {
        new URL(url);
      } catch (e) {
        toast.error('Please enter a valid URL');
        setIsTesting(false);
        return;
      }
      
      // First validate the installation key
      const verifyResponse = await fetch(
        `https://lmniterbzhfihvxsumbl.supabase.co/functions/v1/widget?key=${installKey}&verify=true`
      );
      
      if (!verifyResponse.ok) {
        throw new Error(`HTTP error! Status: ${verifyResponse.status}`);
      }
      
      const verifyData = await verifyResponse.json();
      
      if (!verifyData.valid) {
        setTestResults({
          valid: false,
          error: 'Installation key is invalid or inactive',
          details: {
            key: installKey,
            status: 'Invalid'
          }
        });
        setIsTesting(false);
        return;
      }
      
      // Create a test iframe to check if the widget loads
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;
      
      // Set a timeout for the test
      const timeout = setTimeout(() => {
        document.body.removeChild(iframe);
        setTestResults({
          valid: false,
          error: 'Connection timeout. The website might be unreachable or blocking the test.',
          details: {
            key: installKey,
            url: url,
            status: 'Timeout'
          }
        });
        setIsTesting(false);
      }, 10000);
      
      iframe.onload = () => {
        clearTimeout(timeout);
        
        try {
          // Check if the widget script is loaded
          const hasWidget = iframe.contentWindow?.AccesstopiaWidget !== undefined;
          
          setTestResults({
            valid: hasWidget,
            error: hasWidget ? null : 'Widget not detected on the page',
            details: {
              key: installKey,
              url: url,
              domain: verifyData.domain,
              widgetDetected: hasWidget,
              status: hasWidget ? 'Success' : 'Failed'
            }
          });
        } catch (e) {
          // Cross-origin restrictions might prevent access
          setTestResults({
            valid: false,
            error: 'Could not access the page content due to cross-origin restrictions',
            details: {
              key: installKey,
              url: url,
              status: 'Access Denied'
            }
          });
        }
        
        document.body.removeChild(iframe);
        setIsTesting(false);
      };
      
      iframe.onerror = () => {
        clearTimeout(timeout);
        document.body.removeChild(iframe);
        setTestResults({
          valid: false,
          error: 'Failed to load the website',
          details: {
            key: installKey,
            url: url,
            status: 'Load Error'
          }
        });
        setIsTesting(false);
      };
      
      document.body.appendChild(iframe);
      
    } catch (error) {
      console.error('Test error:', error);
      setTestResults({
        valid: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        details: {
          key: installKey,
          url: testUrl,
          status: 'Error'
        }
      });
      setIsTesting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <header>
        <MainNav currentPath="installation/test" />
      </header>
      <main className="container py-8">
        <Button asChild variant="ghost" className="mb-4">
          <Link to="/installation">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Installation
          </Link>
        </Button>
        
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
          Installation Tester
        </h1>
        
        <p className="text-muted-foreground text-base md:text-lg mb-8">
          Verify if your Accesstopia widget is properly installed on your website.
        </p>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div className="accessibility-glass rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Test Your Installation</h2>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="test-url" className="block text-sm font-medium mb-1">
                  Website URL
                </label>
                <Input
                  id="test-url"
                  placeholder="example.com"
                  value={testUrl}
                  onChange={(e) => setTestUrl(e.target.value)}
                />
              </div>
              
              <div>
                <label htmlFor="install-key" className="block text-sm font-medium mb-1">
                  Installation Key
                </label>
                <Input
                  id="install-key"
                  placeholder="Your installation key"
                  value={installKey}
                  onChange={(e) => setInstallKey(e.target.value)}
                />
              </div>
              
              <Button 
                onClick={handleTest} 
                disabled={isTesting || !testUrl || !installKey}
                className="w-full"
              >
                {isTesting ? 'Testing...' : 'Run Test'}
              </Button>
            </div>
          </div>
          
          <div className="accessibility-glass rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Test Results</h2>
            
            {isTesting ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : testResults ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  {testResults.valid ? (
                    <CheckCircle className="h-6 w-6 text-green-500" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-500" />
                  )}
                  
                  <span className={`text-lg font-semibold ${testResults.valid ? 'text-green-600' : 'text-red-600'}`}>
                    {testResults.valid ? 'Installation Successful' : 'Installation Failed'}
                  </span>
                </div>
                
                {testResults.error && (
                  <Alert variant="destructive">
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>
                      {testResults.error}
                    </AlertDescription>
                  </Alert>
                )}
                
                <div className="bg-muted/50 rounded-md p-4 text-sm">
                  <h3 className="font-medium mb-2">Details</h3>
                  <ul className="space-y-1 text-muted-foreground">
                    {Object.entries(testResults.details).map(([key, value]: [string, any]) => (
                      <li key={key}>
                        <span className="font-medium">{key.charAt(0).toUpperCase() + key.slice(1)}:</span>{' '}
                        {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {!testResults.valid && (
                  <div className="mt-4">
                    <h3 className="font-medium mb-2">Troubleshooting Tips</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      <li>Make sure the installation script is added to the <code>&lt;head&gt;</code> of your website</li>
                      <li>Check if the domain in your installation matches the website URL</li>
                      <li>Verify that your subscription is active</li>
                      <li>Clear your browser cache and try again</li>
                      <li>Check for JavaScript errors in the browser console</li>
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-muted-foreground h-40 flex items-center justify-center">
                <p>Enter your website URL and installation key to test your widget installation.</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4">How It Works</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-muted/50 rounded-lg p-5 text-center flex flex-col items-center">
              <div className="mb-4 flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 text-primary">
                1
              </div>
              <h3 className="font-medium mb-2">Verify Installation Key</h3>
              <p className="text-sm text-muted-foreground text-center">
                We first check if your installation key is valid and active in our system.
              </p>
            </div>
            
            <div className="bg-muted/50 rounded-lg p-5 text-center flex flex-col items-center">
              <div className="mb-4 flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 text-primary">
                2
              </div>
              <h3 className="font-medium mb-2">Check Website Accessibility</h3>
              <p className="text-sm text-muted-foreground text-center">
                We attempt to load your website to verify it's accessible and not blocking our test.
              </p>
            </div>
            
            <div className="bg-muted/50 rounded-lg p-5 text-center flex flex-col items-center">
              <div className="mb-4 flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 text-primary">
                3
              </div>
              <h3 className="font-medium mb-2">Detect Widget</h3>
              <p className="text-sm text-muted-foreground text-center">
                We scan for the Accesstopia widget on your site to confirm it's properly installed and initialized.
              </p>
            </div>
          </div>
        </div>
        
        <div className="mt-12 text-center">
          <h2 className="text-xl font-semibold mb-4">Need Help?</h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-6">
            If you're having trouble with your installation, our support team is here to help.
          </p>
          <Button asChild>
            <a href="mailto:support@accesstopia.com" className="flex items-center gap-2">
              Contact Support <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default InstallTestPage;
